/**
 * Quote Actions — Save, delete, archive, status management
 * Replaces the localStorage-based saveQuote() with cloud persistence via /quotes/* API
 */

const API_BASE = '';  // relative path — Netlify redirects handle routing

/**
 * Get the current site base URL for share links
 */
export function getBaseUrl() {
  return window.location.origin;
}

/**
 * Save current quote to DynamoDB via /quotes/save
 * @param {Object} quoteData - { agentNpn, clientName, clientEmail, ... plans[], agentBranding }
 * @returns {Object} { quoteId, shareToken, shareUrl, createdAt, shareExpiresAt }
 */
export async function saveQuote(quoteData) {
  const response = await fetch(`${API_BASE}/quotes/save`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(quoteData),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Save failed' }));
    throw new Error(error.error || `Save failed: ${response.status}`);
  }

  return response.json();
}

/**
 * Delete a quote permanently
 * @param {string} quoteId
 * @param {string} agentNpn
 */
export async function deleteQuote(quoteId, agentNpn) {
  const response = await fetch(
    `${API_BASE}/quotes/delete?quoteId=${encodeURIComponent(quoteId)}&agentNpn=${encodeURIComponent(agentNpn)}`,
    { method: 'DELETE' }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Delete failed' }));
    throw new Error(error.error || `Delete failed: ${response.status}`);
  }

  return response.json();
}

/**
 * Update quote status (archive, etc.)
 * @param {string} quoteId
 * @param {string} agentNpn
 * @param {string} status - 'draft' | 'sent' | 'viewed' | 'converted' | 'archived'
 */
export async function updateQuoteStatus(quoteId, agentNpn, status) {
  const response = await fetch(`${API_BASE}/quotes/update`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ quoteId, agentNpn, status }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Update failed' }));
    throw new Error(error.error || `Update failed: ${response.status}`);
  }

  return response.json();
}

/**
 * Show a toast notification
 * @param {string} message
 * @param {'success'|'error'|'info'} type
 * @param {Object} [actions] - Optional action buttons { label, onClick }[]
 */
export function showToast(message, type = 'info', actions = []) {
  // Remove any existing toast
  const existing = document.getElementById('wq-toast');
  if (existing) existing.remove();

  const toast = document.createElement('div');
  toast.id = 'wq-toast';
  toast.className = `wq-toast wq-toast--${type}`;

  let actionsHtml = '';
  if (actions.length > 0) {
    actionsHtml = `<div class="wq-toast__actions">
      ${actions.map((a, i) => `<button class="wq-toast__btn" data-action="${i}">${a.label}</button>`).join('')}
    </div>`;
  }

  toast.innerHTML = `
    <div class="wq-toast__content">
      <span class="wq-toast__message"></span>
      <button class="wq-toast__close" aria-label="Close">&times;</button>
    </div>
    ${actionsHtml}
  `;
  toast.querySelector('.wq-toast__message').textContent = message;

  document.body.appendChild(toast);

  // Wire up close button
  toast.querySelector('.wq-toast__close').addEventListener('click', () => toast.remove());

  // Wire up action buttons
  actions.forEach((action, i) => {
    const btn = toast.querySelector(`[data-action="${i}"]`);
    if (btn) btn.addEventListener('click', () => { action.onClick(); toast.remove(); });
  });

  // Auto-dismiss after 8 seconds (unless it has actions)
  if (actions.length === 0) {
    setTimeout(() => toast.remove(), 8000);
  }
}

/**
 * Copy text to clipboard
 * @param {string} text
 */
export async function copyToClipboard(text) {
  await navigator.clipboard.writeText(text);
}

export const QuoteActions = {
  saveQuote,
  deleteQuote,
  updateQuoteStatus,
  showToast,
  copyToClipboard,
  getBaseUrl,
};
